import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCatalog } from '../context/CatalogContext';
import { getTreatmentGuideImage } from '../lib/imageOverrides';
import { buildLocalizedPath } from './LocaleRouter';

/**
 * Curated SEO content section — "Treatment Guides & Resources".
 *
 * Each card is mapped to the most relevant existing destination on the
 * site (treatment detail page, FAQ, or travel hub). When the external
 * articles API CORS issue is resolved, these can be replaced with
 * dynamic `/api/v1/articles` content.
 *
 * No clinic directory links — everything routes back to treatment or
 * coordination content.
 */
const GUIDES = [
  {
    title: 'Cost of Dental Implants in Vietnam',
    excerpt: 'A complete breakdown of dental implant pricing in Vietnam, from single-tooth implants to full-arch All-on-4 procedures.',
    image: 'https://images.pexels.com/photos/305566/pexels-photo-305566.jpeg?auto=compress&cs=tinysrgb&w=900',
    to: '/treatments/dental-implants',
    testid: 'guide-dental-implant-cost',
  },
  {
    title: 'Veneers in Vietnam Guide',
    excerpt: 'Everything you need to know about porcelain veneers in Vietnam — preparation, treatment timeline, and what to expect.',
    image: 'https://images.pexels.com/photos/3845625/pexels-photo-3845625.jpeg?auto=compress&cs=tinysrgb&w=900',
    to: '/treatments/veneers',
    testid: 'guide-veneers',
  },
  {
    title: 'Hair Transplant Recovery Guide',
    excerpt: 'Day-by-day expectations after FUE or DHI hair transplant — healing milestones, aftercare and when results become visible.',
    image: 'https://images.pexels.com/photos/3993239/pexels-photo-3993239.jpeg?auto=compress&cs=tinysrgb&w=900',
    to: '/treatments/hair-transplant',
    testid: 'guide-hair-recovery',
  },
  {
    title: 'Cosmetic Surgery in Vietnam',
    excerpt: 'An international patient overview of cosmetic surgery in Vietnam, including the most requested procedures and what to plan for.',
    image: 'https://images.pexels.com/photos/5327580/pexels-photo-5327580.jpeg?auto=compress&cs=tinysrgb&w=900',
    to: '/treatments/cosmetic-surgery',
    testid: 'guide-cosmetic-surgery',
  },
  {
    title: 'Teeth Whitening in Vietnam',
    excerpt: 'Professional in-clinic teeth whitening explained — types of treatments available, expected results and indicative pricing.',
    image: 'https://images.pexels.com/photos/5069431/pexels-photo-5069431.jpeg?auto=compress&cs=tinysrgb&w=900',
    to: '/treatments/teeth-whitening',
    testid: 'guide-teeth-whitening',
  },
  {
    title: 'Is Vietnam Safe for Medical Treatment?',
    excerpt: 'Questions to ask about provider licensing, accreditation, clinician credentials and treatment risks before choosing care.',
    image: 'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=900',
    to: '/faq',
    testid: 'guide-is-vietnam-safe',
  },
  {
    title: 'How Long Should You Stay in Vietnam for Treatment?',
    excerpt: 'Recommended stay lengths by treatment type — from same-day aesthetics to multi-week recovery for full-arch dental work.',
    image: 'https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=900',
    to: '/travel-hub',
    testid: 'guide-stay-length',
  },
  {
    title: 'Dental Tourism Vietnam Guide',
    excerpt: 'The complete guide to dental tourism in Vietnam — what makes the country a leading destination for international dental care.',
    image: 'https://images.pexels.com/photos/6627569/pexels-photo-6627569.jpeg?auto=compress&cs=tinysrgb&w=900',
    to: '/treatments',
    testid: 'guide-dental-tourism',
  },
];

const TreatmentGuides = () => {
  const { t, language } = useLanguage();
  const { overrides } = useCatalog();
  return (
  <section className="py-20 lg:py-24 bg-white" data-testid="treatment-guides-section">
    <div className="container mx-auto px-4 max-w-7xl">
      <div className="max-w-3xl mb-12 lg:mb-14">
        <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
          {t('guides.eyebrow')}
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
          {t('guides.title')}
        </h2>
        <p className="text-slate-500 mt-4 text-base lg:text-lg leading-relaxed">
          {t('guides.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {GUIDES.map((g) => (
          <Link
            key={g.title}
            to={buildLocalizedPath(g.to, language)}
            className="group bg-white rounded-[20px] overflow-hidden border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
            style={{ boxShadow: '0 2px 12px rgba(15,42,85,0.05)' }}
            data-testid={g.testid}
            aria-label={`Read the ${g.title} guide`}
          >
            <div className="relative overflow-hidden h-44">
              <img
                src={getTreatmentGuideImage(overrides, g.testid, g.image)}
                alt={g.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={(e) => { e.target.src = g.image; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent" />
            </div>
            <div className="p-5 flex flex-col flex-1">
              <h3 className="font-bold text-slate-900 text-[15px] leading-snug mb-2.5 group-hover:text-[#1FA7A1] transition-colors line-clamp-2">
                {g.title}
              </h3>
              <p className="text-slate-500 text-xs leading-relaxed flex-1 line-clamp-3 mb-4">
                {g.excerpt}
              </p>
              <div className="flex items-center gap-1.5 text-[#1FA7A1] font-semibold text-xs mt-auto">
                <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Read guide</span>
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
  );
};

export default TreatmentGuides;
